import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import Back from '../../../components/common/Back';
import Layout from '../Layout';
import { fetchStepTrackerData, deleteStepEntry, addStep, clearMessage } from '../../../redux/slices/customer/stepTrackerSlice';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const StepTracker = () => {
  const dispatch = useDispatch();
  const { steps, currentWeekAverage } = useSelector(state => state.stepTracker);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  useEffect(() => {
    dispatch(clearMessage());
  }, []);

  const [selectedDate, setSelectedDate] = useState(getCurrentDate()); 
  const [updatedSteps, setUpdatedSteps] = useState(steps);
  const [displayCount, setDisplayCount] = useState(6);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState('');
  const [image, setImage] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const openModal = () => {
  setIsModalOpen(true); }
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    dispatch(fetchStepTrackerData());
  }, [dispatch]);

  const fetchFilteredData = () => {
    const filters = { selectedPeriod, startDate, endDate };
    dispatch(fetchStepTrackerData(filters));
  };

   useEffect(() => {
      fetchFilteredData();
      fetchFilteredData();
  }, [dispatch, selectedPeriod, startDate, endDate]);

  const handlePeriodChange = (event) => { setSelectedPeriod(event.target.value); setStartDate(''); setEndDate(''); }
  const handleStartDateChange = (event) => setStartDate(event.target.value);
  const handleEndDateChange = (event) => setEndDate(event.target.value);

  useEffect(() => {
    setUpdatedSteps(steps);
  }, [steps]);

  const chartData = useMemo(() => {
    const labels = updatedSteps.map(entry => entry.check_in_date).reverse();
    const data1 = updatedSteps.map(entry => entry.steps).reverse();
    const data2 = data1.map((value, index) => data1[index] - 5);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Step Line 1',
          data: data2,
          borderWidth: 1,
          pointRadius: 0,
          pointBackgroundColor: '#f4dadb',
          pointStyle: 'images/point.png',
          fill: true,
          borderColor: ["#f4dadb"],
          backgroundColor: '#f4dadb'
        },
        {
          label: '',
          data: data1,
          borderWidth: 3,
          pointStyle: "images/point.png",
          pointRadius: 5,
          pointBackgroundColor: "red",
          labelColor: "green",
          borderColor: ["red"],
          fill: true,
          backgroundColor: '#f8ecec'
        },
      ],
    };
  }, [updatedSteps]);

  const options = useMemo(() => ({
    responsive: true,
    scales: {
      y: {
        title: { display: true, text: 'Step (Kg)' },
        ticks: { beginAtZero: false },
      },
    },
    plugins: {
      legend: { display: true, labels: { color: '#1ea0f7' } },
    },
  }), []);

  const handleDelete = (id, index) => {
    const newSteps = updatedSteps.filter((_, i) => i !== index);    
    dispatch(deleteStepEntry(id)).then(() => setUpdatedSteps(newSteps));
  };

  const handleLoadMore = () => setDisplayCount(prevCount => prevCount + 6);
  const handleDateChange = (event) => setSelectedDate(event.target.value);
  const handleStepChange = (event) => setStep(event.target.value);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    dispatch(addStep({ date: selectedDate, step, image })).then(() => dispatch(fetchStepTrackerData()));
    setSelectedDate(getCurrentDate());
    setStep('');
    setImage(null);
    closeModal();
  };

  return (
    <Layout>
      <main className="main">
        <header className="main-head flex justify-between align-center">
          <h1 className="greet-user-text">Step Tracker</h1>
          <div className="ms-auto">
            <Back />
          </div>
        </header>
        <div className="main-content">
          <div className="main-content_left w-100">
            <div className="chart">
              <div className="tracking-options d-flex flex-wrap align-items-center mb-4">
                {/* Select box for Period */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <select value={selectedPeriod} onChange={handlePeriodChange} className="form-select w-100">
                    <option value="All">All</option>
                    <option value="ThisWeek">This Week</option>
                    <option value="ThisMonth">This Month</option>
                    <option value="Last3Months">Last 3 Months</option>
                    <option value="Last6Months">Last 6 Months</option>
                    <option value="ThisYear">This Year</option>
                  </select>
                </div>

                {/* Calendar picker for Start Date */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="form-select w-100"
                    placeholder="Start Date"
                  />
                </div>

                {/* Calendar picker for End Date */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="form-select w-100"
                    placeholder="End Date"
                  />
                </div>

                {/* Step Tracker Button */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <Link className="btn-curve tracking_links w-100" to="/weight-tracker">
                    Weight Tracker
                  </Link>
                </div>
              </div>
 
              {/* Render the Line chart with the data and options */}
              <Line data={chartData} options={options} />
            </div>
          </div>
          <div id="content-right" className="main-content_right w-100 sec-wg-card pb-3">
            <div className="mt-3">
              <header className="flex align-center justify-between" style={{visibility:'hidden'}}>
                <h3>WEIGHT GOAL</h3>
              </header>
              <div className="wg-wrap">
                <div className="card-wg">
                 
                  <section className="calorie-breakdown mt-3">
                    <div>
                      <button className="btn-curve btn-green d-block w-100" onClick={openModal}>Step Check-in</button>
                    </div>
                  </section>
                </div>
                <div className="card-curve-white mt-3">
                  <section>
                    <div className="text-center">
                      <p>Current Week Average Step</p>
                      <h3 className="mt-2">{currentWeekAverage} KG</h3>
                    </div>
                  </section>
                  <div className="mt-3">
                    <Link className="btn-curve" to="/previous-weeks-average-steps">
                      View Previous Week's Average
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-entries mt-3">
              <header className="text-center">
                <h3>ENTRIES</h3>
              </header>
              <div className="entries-wrap mt-3">
                {/* Entries list */}
                {updatedSteps.slice(0, displayCount).map((entry, index) => (
                  <div className="entries-item" key={index} style={{ display: 'block' }}>
                    <header>
                      <div className="delete-entry" onClick={() => handleDelete(entry.id, index)}>
                        <i className="bi bi-trash3 cursor-pointer"></i>
                      </div>
                      <p>{entry.check_in_date}</p>
                      <div className="step-delete">{entry.steps}</div>
                    </header>
                  </div>
                ))}
              </div>
              {updatedSteps.length > displayCount && (
                <div className="text-center mt-3 load-more-container">
                  <button className="btn-curve" onClick={handleLoadMore}>
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
 
{isModalOpen && (
  <div
    className="modal fade show d-block"
    id="steps-check-in-modal"
    tabIndex="-1"
    aria-labelledby="stepsCheckInLabel"
    aria-hidden="true"
    style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content">
        <div className="modal-header justify-content-center">
          <h5 className="modal-title" id="stepsCheckInLabel">
            Steps Check-In
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={closeModal} // Close modal on click
          />
        </div>
        <div className="modal-body px-md-5 pb-md-4">
          <form onSubmit={handleFormSubmit}>
            <div className="row g-3 gx-md-5 gy-md-4">
              <div className="col-md-6">
                <input
                  name="steps_date"
                  type="date"
                  required
                  className="form-control"
                  value={selectedDate}
                  onChange={handleDateChange} // Handle date change
                />
              </div>
              <div className="col-md-6">
                <input
                  name="steps"
                  type="number"
                  required
                  placeholder="Steps"
                  className="form-control"
                  value={step}
                  onChange={handleStepChange} // Handle step change
                />
              </div>
            </div>
            <div className="d-flex justify-content-center mt-md-4">
              <button type="submit" className="btn-curve">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
)}




    </Layout>
  );
};

export default StepTracker;